/**
 * NavigationStateManager - Utility for preserving navigation state across page transitions
 * 
 * Provides functionality to:
 * - Track and restore scroll positions
 * - Preserve pagination state across navigation
 * - Integrate with browser history for state management
 * - Handle browser refresh and state persistence
 */

import { NavigationState } from '../types/epic-types'

// Storage keys for different state types
const STORAGE_KEYS = {
  EPIC_LIST_STATE: 'epic-list-navigation-state',
  EPIC_DETAILS_STATE: 'epic-details-navigation-state'
} as const

type StorageKey = keyof typeof STORAGE_KEYS

/**
 * NavigationStateManager class for managing navigation state persistence
 */
export class NavigationStateManager {
  private static instance: NavigationStateManager | null = null
  private scrollListeners: Map<string, () => void> = new Map()
  private debounceTimers: Map<string, NodeJS.Timeout> = new Map()

  /**
   * Get singleton instance of NavigationStateManager
   */
  static getInstance(): NavigationStateManager {
    if (!NavigationStateManager.instance) {
      NavigationStateManager.instance = new NavigationStateManager()
    }
    return NavigationStateManager.instance
  }

  /**
   * Save navigation state to sessionStorage
   * @param key - Storage key identifier
   * @param state - Navigation state to save
   */
  saveState(key: string, state: NavigationState): void {
    try {
      const stateWithTimestamp = {
        ...state,
        timestamp: Date.now()
      }
      sessionStorage.setItem(key, JSON.stringify(stateWithTimestamp))
    } catch (error) {
      console.warn('Failed to save navigation state:', error)
    }
  }

  /**
   * Restore navigation state from sessionStorage
   * @param key - Storage key identifier
   * @returns Restored navigation state or null if not found/invalid
   */
  restoreState(key: string): NavigationState | null {
    try {
      const stored = sessionStorage.getItem(key)
      if (!stored) {
        return null
      }

      const parsed = JSON.parse(stored)

      // Validate state structure
      if (!this.isValidNavigationState(parsed)) {
        console.warn('Invalid navigation state structure, clearing stored state')
        this.clearState(key)
        return null
      }

      // Check if state is not too old (1 hour max)
      const maxAge = 60 * 60 * 1000 // 1 hour in milliseconds
      if (parsed.timestamp && (Date.now() - parsed.timestamp) > maxAge) {
        console.debug('Navigation state expired, clearing stored state')
        this.clearState(key)
        return null
      }

      // Return state without timestamp
      const { timestamp, ...state } = parsed
      return state as NavigationState
    } catch (error) {
      console.warn('Failed to restore navigation state:', error)
      this.clearState(key)
      return null
    }
  }

  /**
   * Clear navigation state from sessionStorage
   * @param key - Storage key identifier
   */
  clearState(key: string): void {
    try {
      sessionStorage.removeItem(key)
    } catch (error) {
      console.warn('Failed to clear navigation state:', error)
    }
  }

  /**
   * Clear all navigation states
   */
  clearAllStates(): void {
    Object.values(STORAGE_KEYS).forEach(key => {
      this.clearState(key)
    })
  }

  /**
   * Start tracking scroll position for automatic state saving
   * @param key - Storage key identifier
   * @param getCurrentState - Function to get current navigation state
   * @param debounceMs - Debounce delay in milliseconds (default: 300)
   */
  startScrollTracking(
    key: string,
    getCurrentState: () => NavigationState,
    debounceMs: number = 300
  ): void {
    // Remove existing listener if any
    this.stopScrollTracking(key)

    const handleScroll = () => {
      // Clear existing timer
      const existingTimer = this.debounceTimers.get(key)
      if (existingTimer) {
        clearTimeout(existingTimer)
      }

      // Set new debounced timer
      const timer = setTimeout(() => {
        const currentState = getCurrentState()
        this.saveState(key, currentState)
        this.debounceTimers.delete(key)
      }, debounceMs)

      this.debounceTimers.set(key, timer)
    }

    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true })
    this.scrollListeners.set(key, handleScroll)
  }

  /**
   * Stop tracking scroll position
   * @param key - Storage key identifier
   */
  stopScrollTracking(key: string): void {
    const listener = this.scrollListeners.get(key)
    if (listener) {
      window.removeEventListener('scroll', listener)
      this.scrollListeners.delete(key)
    }

    const timer = this.debounceTimers.get(key)
    if (timer) {
      clearTimeout(timer)
      this.debounceTimers.delete(key)
    }
  }

  /**
   * Restore scroll position to the saved value
   * @param scrollPosition - Scroll position to restore
   * @param smooth - Whether to use smooth scrolling (default: false)
   */
  restoreScrollPosition(scrollPosition: number, smooth: boolean = false): void {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      try {
        window.scrollTo({
          top: scrollPosition,
          behavior: smooth ? 'smooth' : 'auto'
        })
      } catch (error) {
        // Fallback for older browsers
        window.scrollTo(0, scrollPosition)
      }
    })
  }

  /**
   * Get current scroll position
   * @returns Current scroll position
   */
  getCurrentScrollPosition(): number {
    return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0
  }

  /**
   * Save state before navigation (to be called before navigating away)
   * @param key - Storage key identifier
   * @param state - Navigation state to save
   */
  saveBeforeNavigation(key: string, state: NavigationState): void {
    // Update scroll position to current value
    const currentState = {
      ...state,
      scrollPosition: this.getCurrentScrollPosition()
    }
    this.saveState(key, currentState)
  }

  /**
   * Handle browser refresh by saving current state
   * @param key - Storage key identifier
   * @param getCurrentState - Function to get current navigation state
   * @returns Cleanup function to remove event listener
   */
  handleBeforeUnload(key: string, getCurrentState: () => NavigationState): () => void {
    const handleUnload = () => {
      const currentState = getCurrentState()
      this.saveBeforeNavigation(key, currentState)
    }

    window.addEventListener('beforeunload', handleUnload)

    // Return cleanup function
    return () => {
      window.removeEventListener('beforeunload', handleUnload)
    }
  }

  /**
   * Validate navigation state structure
   * @param state - State object to validate
   * @returns True if state is valid NavigationState
   */
  private isValidNavigationState(state: any): state is NavigationState & { timestamp?: number } {
    return (
      state &&
      typeof state === 'object' &&
      typeof state.scrollPosition === 'number' &&
      typeof state.currentPage === 'number' &&
      state.filters &&
      typeof state.filters === 'object' &&
      typeof state.filters.projectKey === 'string' &&
      state.filters.dateRange &&
      typeof state.filters.dateRange === 'object' &&
      typeof state.filters.dateRange.startDate === 'string' &&
      typeof state.filters.dateRange.endDate === 'string'
    )
  }

  /**
   * Cleanup all listeners and timers
   */
  cleanup(): void {
    // Clear all scroll listeners
    this.scrollListeners.forEach((listener, key) => {
      this.stopScrollTracking(key)
    })

    // Clear all timers
    this.debounceTimers.forEach(timer => {
      clearTimeout(timer)
    })
    this.debounceTimers.clear()
  }
}

// Export singleton instance
export const navigationStateManager = NavigationStateManager.getInstance()

// Export storage keys for use in components
export { STORAGE_KEYS }

// Convenience functions for common operations
export const epicListNavigation = {
  save: (state: NavigationState) =>
    navigationStateManager.saveState(STORAGE_KEYS.EPIC_LIST_STATE, state),

  restore: () =>
    navigationStateManager.restoreState(STORAGE_KEYS.EPIC_LIST_STATE),

  clear: () =>
    navigationStateManager.clearState(STORAGE_KEYS.EPIC_LIST_STATE),

  saveBeforeNavigation: (state: NavigationState) =>
    navigationStateManager.saveBeforeNavigation(STORAGE_KEYS.EPIC_LIST_STATE, state),

  startScrollTracking: (getCurrentState: () => NavigationState) =>
    navigationStateManager.startScrollTracking(STORAGE_KEYS.EPIC_LIST_STATE, getCurrentState),

  stopScrollTracking: () =>
    navigationStateManager.stopScrollTracking(STORAGE_KEYS.EPIC_LIST_STATE)
}

export const epicDetailsNavigation = {
  save: (state: NavigationState) =>
    navigationStateManager.saveState(STORAGE_KEYS.EPIC_DETAILS_STATE, state),

  restore: () =>
    navigationStateManager.restoreState(STORAGE_KEYS.EPIC_DETAILS_STATE),

  clear: () =>
    navigationStateManager.clearState(STORAGE_KEYS.EPIC_DETAILS_STATE)
}